import { Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { InputErrors } from '../../forms/errors';
import { ObjectUtil } from '@nomidio/common';
import { Duration } from 'luxon';

export enum TimeUnit {
  Seconds = 'seconds',
  Minutes = 'minutes',
  Hours = 'hours',
  Days = 'days',
}

@Component({
  selector: 'app-auth-max-age-input',
  templateUrl: './auth-max-age-input.component.html',
  styleUrls: ['./auth-max-age-input.component.scss'],
  standalone: false
})
export class AuthMaxAgeInputComponent {
  public readonly timeUnits = Object.keys(TimeUnit).map(key => TimeUnit[key]);

  public readonly SECONDS_IN_MINUTE = 60;
  public readonly SECONDS_IN_HOUR = this.SECONDS_IN_MINUTE * 60;
  public readonly SECONDS_IN_DAY = this.SECONDS_IN_HOUR * 24;

  @Input()
  public formGroup: FormGroup;

  @Input()
  public name: string = '';

  @Input()
  public errors: InputErrors = {
    maxAge: (error) => `The maximum age is ${error.max} ${error.units}`,
    minAge: (error) => `The minimum age is ${error.min} ${error.units}`,
    required: 'This field is required',
    pattern: 'Numbers only',
  };

  public get number() { return this.formGroup?.get('number'); }
  public get unit() { return this.formGroup?.get('unit'); }

  public get value(): number | null {
    return AuthMaxAgeInputComponent.inputsToSeconds(this.formGroup);
  }

  public get showWarning(): boolean {
    return this.value > this.SECONDS_IN_DAY && this.formGroup?.valid;
  }

  public static inputsToSeconds(formGroup: FormGroup): number | null {
    const number = formGroup?.get('number').value;
    const unit = formGroup?.get('unit').value;
    if ((!number && number !== 0) || !unit) {
      return null;
    } else {
      const duration = Duration.fromObject({
        [unit]: number,
      });
      return duration.as('seconds');
    }
  }

  public static secondsToInputs(seconds: number | null ): { number: number | null, unit: TimeUnit } {
    if (seconds === null || seconds === undefined) {
      return {
        number: null,
        unit: TimeUnit.Hours,
      };
    } else if (typeof seconds === 'number') {
      if (seconds === 0) {
        return { number: 0, unit: TimeUnit.Seconds };
      }

      const units = [TimeUnit.Days, TimeUnit.Hours, TimeUnit.Minutes, TimeUnit.Seconds];
      const { unit, secondsInUnit } = units
        .map(unit => {
          const secondsInUnit = Duration.fromObject({ [unit]: 1 }).as('seconds');
          return { unit, secondsInUnit };
        })
        .find(({ secondsInUnit }) => seconds % secondsInUnit === 0);

      return {
        number: seconds / secondsInUnit,
        unit
      };
    } else {
      throw Error('invalid value type');
    }
  }
}

export function AuthMaxAgePresentation(seconds: number): string {
  if (ObjectUtil.isNullOrUndefined(seconds)) {
    return '';
  }
  const { number, unit } = AuthMaxAgeInputComponent.secondsToInputs(seconds);
  const unitDisplay = number === 1 ? unit.slice(0, unit.length - 1) : unit;
  return `${number} ${unitDisplay}`;
}
